import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

interface IdenticonCell {
  x: number;
  y: number;
}

/**
 * A GitHub-style identicon derived from a display name: a 5×5 grid, mirrored down the middle, with
 * the pattern and the hue both hashed from the name. Deterministic and offline — the same name always
 * yields the same badge, and nothing is fetched.
 *
 * The grid is drawn on a 12×12 canvas: 5 cells of 2 units, inset by 1 unit of padding on each side,
 * which is the proportion GitHub uses.
 */
@Component({
  selector: 'app-identicon',
  standalone: true,
  template: `
    <svg viewBox="0 0 12 12" [attr.width]="size()" [attr.height]="size()"
         role="img" [attr.aria-label]="name() || 'Unknown user'">
      <rect width="12" height="12" [attr.fill]="background()" />
      @for (cell of cells(); track $index) {
        <rect [attr.x]="cell.x" [attr.y]="cell.y" width="2" height="2" [attr.fill]="foreground()" />
      }
    </svg>
  `,
  styles: [`
    :host { display: inline-flex; }
    /* border-box keeps the hairline inside the requested size, so a 36px identicon stays 36px. */
    svg {
      display: block;
      box-sizing: border-box;
      border: 1px solid #d0d0d0;
      border-radius: 20%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IdenticonComponent {
  readonly name = input<string | null | undefined>('');
  /** Rendered edge length in px. */
  readonly size = input<number>(36);

  /**
   * Two independent 32-bit FNV-1a hashes of the name: the first supplies the 15 pattern bits, the
   * second the hue. Case- and whitespace-insensitive, so "Jane Doe" and "jane doe" match.
   */
  private readonly hash = computed<[number, number]>(() => {
    const value = (this.name() ?? '').trim().toLowerCase();
    const words: number[] = [];
    for (let seed = 0; seed < 2; seed++) {
      let h = 0x811c9dc5 ^ Math.imul(seed, 0x01000193);
      for (let i = 0; i < value.length; i++) {
        h ^= value.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
      }
      words.push(h >>> 0);
    }
    return [words[0], words[1]];
  });

  /**
   * The filled cells. Only the left three columns are hashed (15 bits); columns 3 and 4 mirror
   * columns 1 and 0, which is what gives an identicon its symmetry.
   */
  readonly cells = computed<IdenticonCell[]>(() => {
    const [pattern] = this.hash();
    const filled: IdenticonCell[] = [];

    for (let col = 0; col < 3; col++) {
      for (let row = 0; row < 5; row++) {
        if (!((pattern >>> (col * 5 + row)) & 1)) continue;

        const mirror = 4 - col;
        filled.push({ x: 1 + col * 2, y: 1 + row * 2 });
        if (mirror !== col) filled.push({ x: 1 + mirror * 2, y: 1 + row * 2 });
      }
    }

    return filled;
  });

  private readonly hue = computed(() => this.hash()[1] % 360);

  readonly foreground = computed(() => `hsl(${this.hue()}, 52%, 48%)`);
  readonly background = computed(() => `hsl(${this.hue()}, 30%, 94%)`);
}
